<script lang="ts" setup>
import {
  useDialog,
  useToast
} from '#imports'

const { openDialog } = useDialog()
const { showError } = useToast()

import type { Tables } from '~~/types/database.types.ts';
enum Version {
  large = 'large',
  small = 'small',
}
const {
  spot = undefined,
  showStatus = false,
  showTier = false,
  showDestination = false,
  to = undefined,
  version = Version.large,
  actions = false,
  creator = undefined
} = defineProps<{
  spot: Tables<'spots'> | undefined;
  showStatus?:boolean;
  showTier?: boolean;
  showDestination?: boolean;
  to?: string;
  version?: string;
  actions?: boolean;
  creator?: Tables<'profiles'> | undefined;
}>()

const openShareDialog = () => {
  if (!spot) return;
  try {
    openDialog('MPShare', {
      map: spot,
      subtitle: 'Share spot:',
      version: 'spot',
    });
  } catch (e) {
    showError('Error opening share dialog');
  }
};
</script>

<template>
<div :class="['spot', version]">
  <NuxtLinkLocale :to="to ?? `/maps/${spot.map_slug?.current_slug}/spots/${spot?.current_slug}`">
    <div class="image">
      <MPImg :src="spot.cover_image_url"/>
      <div v-if="creator" class="spot-creator">
        <MPImg :src="creator.profile_image_url"/>
        <span><small>Selected by</small> @{{ creator.mappr_handle }}</span>
      </div>
    </div>
  </NuxtLinkLocale>
  <div class="actions-container" v-if="actions">
    <div class="actions">
      <MPBookmark
        size="small"
        version="secondary"
        link
        bookmark="spot_bookmarks"
        tooltip
        background-color="var(--color-slate-100)"
        icon-color="var(--color-rose-500)"
        :spot-id="spot.id"
      />
      <MPButton
        icon="ri-share-line"
        version="secondary"
        size="small"
        rounded
        link
        hide-label
        background-color="var(--color-slate-100)"
        @click="openShareDialog()"
      />
    </div>
  </div>
  <NuxtLinkLocale class="content-wrapper"  :to="to ?? `/maps/${spot.map_slug?.current_slug}/spots/${spot?.current_slug}`">
    <div class="content">
      <div>
        <div v-if="spot.cities && showDestination" class="city">
          {{ spot.cities.name }}
        </div>
        <div class="name" v-if="spot.name">{{ spot.name }}</div>
        <div class="summary" v-if="spot.summary">
          {{ spot.summary }}
        </div>
      </div>
      <div class="info">
        <MPTag v-if="spot.categories" class="category" rounded>
          <i :class="spot.categories.icon" />
          {{ spot.categories.name.length > 12
            ? spot.categories.name.substring(0, 12) + "&hellip;"
            : spot.categories.name
          }}
        </MPTag>
        <MPTag
          v-if="spot.visibility && spot.visibility !== 'public' && showTier"
          :class="['visibility', spot.visibility]"
          rounded
        >
          {{
            spot.visibility === 'supporter' ? 'supporter' :
            spot.visibility === 'subscriber' ? 'premium' :
            spot.visibility === 'authenticated' ? 'members' : ''
          }}&nbsp;Only
        </MPTag>
      </div>
    </div>
  </NuxtLinkLocale>
 </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/typography.pcss';

.spot {
  display: flex;
  flex-direction: column;
  /* min-width: 18rem; */
  max-width: 20rem;
  min-width: 10rem;
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
}

.spot.small {
  min-width: 100%;
  max-width: auto;
  display: grid;
  grid-template-columns: 40% 60%;
}


.image {
  position: relative;
  > img {
    width: 100%;
    height: auto;
    height: 13em;
    object-fit: cover;
  }
}

.spot-creator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 0.5rem 1rem 1rem;
  display: flex;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 60%);
  backdrop-filter: blur(1px);
  align-items: center;
  justify-content: left;
  gap: 0.6rem;
  color: var(--color-white);
  span {
    @mixin typeface-regular;
    line-height: 1.1;
  }
  small {
    display: block;
    color: var(--color-slate-300);
    font-size: 0.9em;
  }
  img {
    object-fit: cover;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 100%;
  }
}

.small .image {
  max-width: 100%;
  height: 100%;

  img {
    height: 100%;
  }
}

.actions-container {
  position: relative;

  .actions {
    position: absolute;
    display: flex;
    gap: 0.75rem;
    top: -1.25rem;
    right: 0.75rem;
    button {
      border: 1px solid var(--color-slate-200) !important;
      border-radius: 50%;
    }
  }
  .actions:deep( button) {
    border: 1px solid var(--color-slate-200) !important;
    border-radius: 50%;
  }
}


.content-wrapper {
  height: 100%;
}

.small {
  position: relative;
}

.small .actions-container {
  position: absolute;
  right: -0.5rem;
  bottom: -0.5rem;
  .actions {
    position: relative;
    display: flex;
    gap: 0.5rem;
  }
}

.small .actions:deep(button),
.small .actions:deep(i) {
  background: transparent !important;
  padding: 0 !important;
  /* border: none !important; */
}


.content {
  max-width: 100%;
  height: 100%;
  background-color: var(--color-slate-50);
  padding: 1rem 1.45rem;
  display: flex;
  flex-direction: column;
  justify-items: start;
  gap: 0.75rem;
  color: var(--color-slate-800);
  justify-content: space-between;
  > * {
    width: fit-content;
  }
}

.small .content {
  padding: 1rem;
}

.city {
  @mixin typeface-small;
  font-weight: 500;
}

.category {
  @mixin typeface-small;
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background-color: white;
  color: var(--color-slate-800);
  i {
    color: var(--color-slate-400);
  }
}

.summary {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  @mixin typeface-regular;
  line-height: 1.45rem;
  color: var(--color-slate-500);
}

.name {
  font-size: 1.2rem;
  color: var(--color-slate-900);
  font-weight: 400;
  margin: 0.5rem 0;
}

.visibility {
  text-transform: capitalize;
  margin-left: -0.25rem;
  i {
    color: var(--color-yellow-400);
  }
}

.visibility.supporter {
  background-color: transparent;
  color: var(--color-rose-500);
  padding: 0 0.25rem;
}

.visibility.subscriber {
  background-color: transparent;
  color: var(--color-indigo-500);
}

.info {
  margin: 0.75rem 0 0;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  white-space: no-wrap;
  justify-self: end;
}

.small .info {
  margin: 1.25rem 0 0;
}

.small .summary {
  line-height: 1.35rem;
  margin-bottom: 0.5rem;
}

.small .name {
  margin: 0.25rem 0;
}
</style>
